/* ADC Sheet sub-panel — ADC(I) SHEET FOR EXPORT per invoice, auto-made for Emirates / DHL / FedEx / UPS slips. */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Eye, FileSpreadsheet, Loader2, Printer, Search, Trash2, Upload } from "lucide-react";
import { openPdf } from "@/components/EdfDocPanel";
import { printPdfUrl } from "@/components/InvoiceDocExtras";

const H = () => ({ headers: getAuthHeader() });
export const COURIER_RX = /EMIRATES|DHL|FED\s*EX|\bUPS\b/i;

export const printAdc = async (invoiceNo) => {
  try {
    const r = await axios.get(`${API}/adc-doc/by-invoice/${encodeURIComponent(invoiceNo)}/download`, { ...H(), responseType: "blob" });
    printPdfUrl(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })));
  } catch (e) { toast.error("ADC sheet not made yet for this invoice"); }
};

const AdcRow = ({ r, isAdmin, onDelete }) => (
  <tr className="border-b last:border-0 hover:bg-slate-50" data-testid={`adc-row-${r.invoice_no}`}>
    <td data-label="Invoice" className="py-2 font-mono font-semibold text-indigo-700">{r.invoice_no}</td>
    <td data-label="Date">{r.invoice_date}</td>
    <td data-label="Method"><span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">{r.method}</span></td>
    <td data-label="Consignee" className="max-w-[180px] truncate" title={`${r.buyer} · ${r.country}`}>{r.buyer}<div className="text-[10px] text-slate-400">{r.country}</div></td>
    <td data-label="Company" className="text-xs text-slate-600">{r.company_name}</td>
    <td data-label="Qty" className="text-right">{r.qty_total}</td>
    <td data-label="FOB ($)" className="pr-3 text-right font-semibold" data-testid={`adc-fob-${r.invoice_no}`}>${Number(r.fob_usd || 0).toFixed(2)}</td>
    <td data-label="Source"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${r.source === "order_register" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{r.source === "order_register" ? "AUTO" : "MANUAL"}</span></td>
    <td data-label="Actions" className="whitespace-nowrap text-right">
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="View" onClick={() => openPdf(r.id, true, "adc-doc")} data-testid={`adc-view-${r.invoice_no}`}><Eye className="h-4 w-4" /></Button>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Download" onClick={() => openPdf(r.id, false, "adc-doc")} data-testid={`adc-dl-${r.invoice_no}`}><Download className="h-4 w-4" /></Button>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Print" onClick={() => printAdc(r.invoice_no)} data-testid={`adc-print-${r.invoice_no}`}><Printer className="h-4 w-4" /></Button>
      {isAdmin && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => onDelete(r)} data-testid={`adc-del-${r.invoice_no}`}><Trash2 className="h-4 w-4" /></Button>}
    </td>
  </tr>
);

export default function AdcDocPanel({ user }) {
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [up, setUp] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    axios.get(`${API}/adc-doc/history`, { ...H(), params: { q } }).then(r => setRows(r.data.rows))
      .catch(e => { setRows([]); toast.error(e.response?.data?.detail || "Could not load ADC sheets"); });
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const upload = async (f) => {
    if (!f) return;
    setUp(true);
    const fd = new FormData(); fd.append("packing_slip", f);
    try { const r = await axios.post(`${API}/adc-doc/generate`, fd, H()); toast.success(`ADC sheet ${r.data.invoice_no} made`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not read this packing slip"); }
    setUp(false); if (fileRef.current) fileRef.current.value = "";
  };
  const del = async (r) => {
    if (!window.confirm(`Delete ADC sheet ${r.invoice_no}?`)) return;
    await axios.delete(`${API}/adc-doc/history/${r.id}`, H()).catch(() => toast.error("Delete failed")); load();
  };
  return (
    <div className="space-y-5" data-testid="adc-doc-panel">
      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800"><FileSpreadsheet className="h-5 w-5 text-indigo-600" /> ADC Sheet</h2>
        <p className="mt-1 text-sm text-slate-500">Made automatically for Emirates, DHL, FedEx and UPS invoices when the packing slip is uploaded in Order Register. Products, batch, dates, qty and FOB ($) come from the slip.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="adc-upload-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={up} className="bg-indigo-600 hover:bg-indigo-700" data-testid="adc-upload-btn">
            {up ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload packing slip
          </Button>
          <span className="text-xs text-slate-400">Re-uploading the same invoice replaces its ADC sheet.</span>
        </div>
      </div>
      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h3 className="font-semibold text-slate-700">ADC sheets</h3>
          <div className="relative ml-auto w-72">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice, buyer, method" className="pl-8" data-testid="adc-search" />
          </div>
        </div>
        {rows === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="adc-empty">No ADC sheets yet.</p>) : (
          <div className="overflow-x-auto">
            <table className="doc-cards w-full text-sm" data-testid="adc-table">
              <thead><tr className="border-b text-left text-xs text-slate-500">
                <th className="py-2">Invoice</th><th>Date</th><th>Method</th><th>Consignee</th><th>Company</th><th className="text-right">Qty</th><th className="pr-3 text-right">FOB ($)</th><th>Source</th><th className="text-right">Actions</th>
              </tr></thead>
              <tbody>{rows.map(r => <AdcRow key={r.id} r={r} isAdmin={isAdmin} onDelete={del} />)}</tbody>
            </table>
          </div>)}
      </div>
    </div>
  );
}
